import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext.jsx';
import { useSettings } from '../context/SettingsContext.jsx';
import { NAV } from '../data/navTabs.js';

// Per-result-type icon. `tab` comes from the backend, but we keep an icon map here.
const TYPE = {
  goal: '🎯', note: '📝', trip: '🧳', project: '💡', idea: '💡', wish: '🎁', term: '📖',
};

// Global command palette. Opens on ⌘/Ctrl-K or the `open_command_palette` window
// event (dispatched by the header search button). Searches the user's data via
// /api/search and also doubles as a quick page launcher. onNavigate = switchTab.
export default function CommandPalette({ onNavigate }) {
  const { apiFetch } = useAuth();
  const { t } = useSettings();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(0);

  const inputRef = useRef(null);
  const reqId = useRef(0);
  const itemsRef = useRef([]);
  const selRef = useRef(0);
  selRef.current = sel;
  const lastPos = useRef({ x: -1, y: -1 });   // last real cursor position (to ignore scroll-fired hovers)


  const pageLabel = (id, fallback) => { const k = 'nav_' + id; const v = t(k); return v && v !== k ? v : fallback; };
  const term = q.trim();
  const lc = term.toLowerCase();

  const pages = NAV
    .filter(n => term.length < 2 ? true : (pageLabel(n.id, n.label).toLowerCase().includes(lc) || n.id.includes(lc)))
    .map(n => ({ kind: 'page', id: n.id, icon: n.icon, label: pageLabel(n.id, n.label) }));
  const res = results.map(r => ({ kind: 'result', ...r, icon: TYPE[r.type] || '•' }));
  const items = term.length < 2 ? pages : [...pages, ...res];
  itemsRef.current = items;
  const curSel = items.length ? Math.min(sel, items.length - 1) : 0;

  const close = () => { setOpen(false); setQ(''); setResults([]); setSel(0); setLoading(false); };
  const activate = (item) => {
    if (!item) return;
    const tab = item.kind === 'page' ? item.id : item.tab;
    const focus = item.kind === 'result' ? item.id : undefined;
    close();
    if (tab && onNavigate) onNavigate(tab, focus);
  };

  // ⌘/Ctrl-K toggle + open event + in-overlay keyboard nav.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); setOpen(o => !o); return; }
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(s + 1, itemsRef.current.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(s - 1, 0)); }
      else if (e.key === 'Enter') { e.preventDefault(); activate(itemsRef.current[selRef.current]); }
    };
    const onOpenEvt = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('open_command_palette', onOpenEvt);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('open_command_palette', onOpenEvt); };
  }, [open]);

  // Focus the input when opened.
  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  // Lock the page behind the palette so the background can't scroll and taps on the
  // backdrop aren't swallowed by a scroll gesture (the position:fixed trick is the
  // only thing that reliably stops scroll on iOS Safari).
  useEffect(() => {
    if (!open) return undefined;
    const y = window.scrollY;
    const b = document.body.style;
    const saved = { position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow };
    b.position = 'fixed'; b.top = `-${y}px`; b.left = '0'; b.right = '0'; b.width = '100%'; b.overflow = 'hidden';
    return () => {
      b.position = saved.position; b.top = saved.top; b.left = saved.left;
      b.right = saved.right; b.width = saved.width; b.overflow = saved.overflow;
      window.scrollTo(0, y);
    };
  }, [open]);

  // Debounced search; stale responses are ignored via a request id.
  useEffect(() => {
    if (!open) return undefined;
    setSel(0);
    if (term.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const myReq = ++reqId.current;
    const ctrl = new AbortController();
    const id = setTimeout(async () => {
      try {
        const r = await apiFetch('/search?q=' + encodeURIComponent(term), { signal: ctrl.signal });
        if (myReq !== reqId.current) return;            // a newer query superseded this one
        if (r && r.ok) { const data = await r.json(); setResults(Array.isArray(data.results) ? data.results : []); }
        else setResults([]);
      } catch { if (myReq === reqId.current) setResults([]); }
      if (myReq === reqId.current) setLoading(false);
    }, 320);
    return () => { clearTimeout(id); ctrl.abort(); };  // cancel an in-flight/queued request on new keystroke or close
  }, [q, open, apiFetch]);

  if (!open) return null;

  return (
    <div onClick={close}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(4,6,11,0.82)',
               display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
               overscrollBehavior: 'contain', padding: '12vh 16px 16px' }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 560, background: 'var(--app-card,#121216)',
                 border: '1px solid #ffffff14', borderRadius: 14, overflow: 'hidden',
                 boxShadow: '0 20px 60px rgba(0,0,0,0.5)', animation: 'up 0.12s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid #ffffff0d' }}>
          <span style={{ fontSize: 16, opacity: 0.7 }}>🔍</span>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('search_placeholder', 'Поиск по всему…')}
            style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--app-text,#ece6d6)',
                     fontSize: 15, padding: '2px 0' }} />
          {loading && <span style={{ fontSize: 11, color: '#8a8a92' }}>…</span>}
          <kbd style={{ fontSize: 10, color: '#667', border: '1px solid #ffffff14', borderRadius: 5, padding: '2px 6px' }}>ESC</kbd>
        </div>

        <div style={{ maxHeight: '52vh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', padding: 6 }}>
          {items.length === 0 && (
            <div style={{ padding: '22px 14px', textAlign: 'center', color: '#8a8a92', fontSize: 13 }}>
              {term.length < 2
                ? (t('search_hint', 'Введите запрос — найду цели, заметки, поездки, идеи…'))
                : (loading ? '…' : (t('search_none', 'Ничего не найдено')))}
            </div>
          )}
          {items.map((it, i) => {
            const isSel = i === curSel;
            const label = it.kind === 'page' ? it.label : (it.label || '—');
            return (
              <div key={(it.kind) + (it.id || i)}
                onMouseMove={(e) => {
                  if (e.clientX === lastPos.current.x && e.clientY === lastPos.current.y) return; // scroll, not a real move
                  lastPos.current = { x: e.clientX, y: e.clientY };
                  if (sel !== i) setSel(i);
                }}
                onClick={() => activate(it)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 9,
                         cursor: 'pointer', transition: 'none', background: isSel ? 'rgba(255,255,255,0.10)' : 'transparent' }}>
                <span style={{ fontSize: 17, width: 22, textAlign: 'center', flexShrink: 0 }}>{it.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--app-text,#ece6d6)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                  {it.kind === 'result' && it.sub && (
                    <div style={{ fontSize: 11, color: '#8a8a92', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.sub}</div>
                  )}
                </div>
                <span style={{ fontSize: 10, color: '#667', flexShrink: 0 }}>
                  {it.kind === 'page'
                    ? (t('search_go', 'перейти'))
                    : (t('nav_' + it.tab) !== 'nav_' + it.tab ? t('nav_' + it.tab) : it.tab)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
